import React, { Fragment, useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { AuthActions } from "src/redux/Auth/actions";
import { ModalActions } from "src/redux/Modal/actions";
import PrivacyPolicy from "../../PrivacyPolicy";
import TermsOfUse from "../../TermsOfUse";
import CheckBox from "src/components/common/CheckBox";
import Button from "src/components/common/Button";
import styles from "./styles.module.scss";
import { SpinnerActions } from "src/redux/Spinner/actions";

const PASSWORD_UPPERCASE_RE = /[A-Z]/;
const PASSWORD_LOWERCASE_RE = /[a-z]/;
const PASSWORD_SPECIAL_RE = /[.,<>)(\][!@#$%^&*]/;

export const validatePassword = (password, verify) => {
    if (password.length < 8) {
        return "Password not long enough";
    }

    if (verify !== password) {
        return "Passwords do not match";
    }

    if (!PASSWORD_UPPERCASE_RE.test(password)) {
        return "Password does not contain any uppercase characters";
    }

    if (!PASSWORD_LOWERCASE_RE.test(password)) {
        return "Password does not contain any lowercase characters";
    }

    if (!PASSWORD_SPECIAL_RE.test(password)) {
        return "Password does not contain any special characters";
    }

    return "";
};

export default () => {
    const dispatch = useDispatch();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [verify, setVerify] = useState("");
    const [phone, setPhone] = useState("");
    const [givenName, setGivenName] = useState("");
    const [familyName, setFamilyName] = useState("");
    const [terms, setTerms] = useState(false);
    const [policy, setPolicy] = useState(false);

    const [disabled, setDisabled] = useState(true);

    const [error, setError] = useState(null);

    const handleSignUpClick = (e) => {
        e.preventDefault();

        dispatch(SpinnerActions.Open());
        dispatch(
            AuthActions.SignUp({
                email,
                password,
                verifiedPassword: verify,
                phoneNumber: phone,
                givenName,
                familyName,
            })
        )
            .then(() => {
                dispatch(
                    ModalActions.Open(Confirm, {
                        small: true,
                        title: "Next Steps",
                        customProps: { email },
                        onClose: () => dispatch(ModalActions.CloseAll()),
                    })
                );
            })
            .catch((error) => {
                setError(error.message);
            })
            .finally(() => {
                dispatch(SpinnerActions.Close());
            });
    };

    const handlePrivacyClick = () => {
        dispatch(
            ModalActions.Open(PrivacyPolicy, {
                wide: true,
                title: "TRU Technologies Privacy Policy",
                long: true,
            })
        );
    };

    const handleTermsClick = () => {
        dispatch(
            ModalActions.Open(TermsOfUse, {
                wide: true,
                title: "TRU Technologies Terms of Use",
                long: true,
            })
        );
    };

    // we dont want to call this on initial mount
    // https://stackoverflow.com/questions/53253940/make-react-useeffect-hook-not-run-on-initial-render
    const firstUpdate = useRef(true);

    useEffect(() => {
        if (firstUpdate.current) {
            firstUpdate.current = false;
            return;
        }

        setError(null);
        setDisabled(true);

        if (!/.+@.+/.test(email)) {
            setError("Invalid Email");
            return;
        }

        const passwordError = validatePassword(password, verify);
        if (passwordError.length > 0) {
            setError(passwordError);
            return;
        }

        if (phone.length === 0) {
            setError("Invalid Phone Number");
            return;
        }

        if (givenName.length === 0) {
            setError("Invalid Given Name");
            return;
        }

        if (familyName.length === 0) {
            setError("Invalid Family Name");
            return;
        }

        if (!terms) {
            setError("Must accept Terms of Use");
            return;
        }

        if (!policy) {
            setError("Must accept Privacy Policy");
            return;
        }

        setDisabled(false);
    }, [email, password, verify, phone, givenName, familyName, terms, policy]);

    return (
        <form className={styles.signup}>
            <h1>Sign Up</h1>

            <div className={styles.splitter} />

            <div className={styles.body}>
                <label>
                    Work email address
                    <input
                        type="email"
                        autoFocus
                        placeholder="name@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        data-testid="email-input"
                    />
                </label>

                <PasswordAndVerify
                    password={password}
                    setPassword={setPassword}
                    verify={verify}
                    setVerify={setVerify}
                />

                <label>
                    Phone Number
                    <input
                        type="text"
                        value={phone}
                        placeholder="+8201012341234"
                        onChange={(e) => setPhone(e.target.value)}
                        required
                        data-testid="phone-number-input"
                    />
                </label>

                <label>
                    Given Name
                    <input
                        type="text"
                        value={givenName}
                        onChange={(e) => setGivenName(e.target.value)}
                        required
                        data-testid="given-name-input"
                    />
                </label>

                <label>
                    Family Name
                    <input
                        type="text"
                        value={familyName}
                        onChange={(e) => setFamilyName(e.target.value)}
                        required
                        data-testid="family-name-input"
                    />
                </label>

                <div className={styles.splitter} />

                <div className={styles.legal}>
                    <CheckBox
                        name="terms"
                        checked={terms}
                        onChange={() => setTerms(!terms)}
                        dataTestId="signup_checkbox_terms"
                        small
                        styleName="black"
                    >
                        <span className={styles.info}>
                            {`I accept the `}
                            <span
                                className={styles.highlight}
                                onClick={handleTermsClick}
                            >
                                Terms of Use
                            </span>
                        </span>
                    </CheckBox>

                    <CheckBox
                        name="policy"
                        checked={policy}
                        onChange={() => setPolicy(!policy)}
                        dataTestId="signup_checkbox_policy"
                        small
                        styleName="black"
                    >
                        <span className={styles.info}>
                            {`I accept the `}
                            <span
                                className={styles.highlight}
                                onClick={handlePrivacyClick}
                            >
                                Privacy Policy
                            </span>
                        </span>
                    </CheckBox>
                </div>

                <div className={styles.splitter} />
            </div>

            {error && (
                <div className={styles.error} data-testid="error-div">
                    {error}
                </div>
            )}

            <div className={styles.footer}>
                <Button
                    value="Sign Up"
                    disabled={disabled}
                    onClick={handleSignUpClick}
                    dataTestId="signup_button_signuUp"
                />
            </div>
        </form>
    );
};

const Confirm = ({ email }) => (
    <div className={styles.confirm}>
        <p>An email has been sent to: </p>
        <br />
        <p>
            <b>{email}</b>
        </p>
        <br />
        <p>
            Please follow the instructions in the email to complete your signup.
        </p>
    </div>
);

export const PasswordAndVerify = ({
    password,
    setPassword,
    verify,
    setVerify,
}) => (
    <Fragment>
        <label>
            Password
            <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
            />
            <ul className={styles.passwordRequirements}>
                <li className={password.length >= 8 ? styles.valid : undefined}>
                    At least 8 characters
                </li>
                <li
                    className={
                        PASSWORD_UPPERCASE_RE.test(password)
                            ? styles.valid
                            : undefined
                    }
                >
                    1 uppercase
                </li>
                <li
                    className={
                        PASSWORD_LOWERCASE_RE.test(password)
                            ? styles.valid
                            : undefined
                    }
                >
                    1 lowercase
                </li>
                <li
                    className={
                        PASSWORD_SPECIAL_RE.test(password)
                            ? styles.valid
                            : undefined
                    }
                >
                    1 symbol
                </li>
            </ul>
        </label>

        <label>
            Verify Password
            <input
                type="password"
                value={verify}
                onChange={(e) => setVerify(e.target.value)}
                required
                data-testid="verify-password-input"
            />
        </label>
    </Fragment>
);
